import { formatCurrency } from "@/common/utils/formatters";
import Accordion from "@/common/components/Accordion";
import TextField from "@/common/components/TextField";
import Checkbox from "@/common/components/Checkbox";
import { CompanyBranchData, CompanyData } from "@/features/company/types/company.type";
import { useClientCompaniesAvailableToAllocateInBranchSummary } from '../../hooks/useClientCompaniesAvailableToAllocateInBranchSummary';
import { ClientCompanyAllowedCategoriesAndProducts } from '../../types/allowedCategoriesAndProducts.type';
import { CreditProductCategoryConfig } from './creditProductCategoryConfig';
import { CompanyContractConsumptionControlledBy } from "@/features/company/types/contract.type";

interface CreditConfigurationProps {
  branchData?: CompanyBranchData
  companyData?: CompanyData
}

export const CreditConfiguration = ({ branchData, companyData }: CreditConfigurationProps) => {
  const {
    data: allowedCategoriesAndProducts,
  } = useClientCompaniesAvailableToAllocateInBranchSummary<ClientCompanyAllowedCategoriesAndProducts>({});

  const controlledBy =
    companyData?.current_contract?.consumption_controlled_by === CompanyContractConsumptionControlledBy.Quantity
      ? CompanyContractConsumptionControlledBy.Quantity
      : CompanyContractConsumptionControlledBy.Currency;

  return (
    <div className="flex flex-col gap-4">
      <p className="font-bold text-lg">
        {companyData?.kind === 'public_company' ? 'Configurações de Cota' : 'Configurações de crédito'}
      </p>

      <div className="flex flex-col gap-2">
        <TextField
          name="granted_credit"
          label={companyData?.kind === 'public_company' ? 'Cota de empenho da filial' : 'Crédito garantido a filial'}
          value={formatCurrency(Number(branchData?.granted_credit))}
          disabled
        />

        <div className="flex items-center gap-4">
          <p className="text-[12px] font-light italic">
            {companyData?.kind === 'public_company' ? 'Cota Global do Contrato' : 'Crédito garantido a empresa'}:{' '}
            {formatCurrency(Number(companyData?.granted_credit || 0))}
          </p>

          <p className="text-[12px] font-light italic">
            {companyData?.kind === 'public_company' ? 'Cota livre para alocação' : 'Crédito livre para alocação'}:{' '}
            {formatCurrency(Number(companyData?.unallocated_credit || 0))}
          </p>

          {branchData?.consumed_credit && (
            <p className="text-[12px] font-light italic">
              Crédito consumido:{' '}
              {formatCurrency(Number(branchData?.consumed_credit))}
            </p>
          )}
        </div>
      </div>

      {branchData?.product_category_granted_credits?.length ? (
        <Accordion
          name="product_category_granted_credits"
          head={(
            <Checkbox
              label="Crédito garantido por serviço"
              checked={!!branchData?.product_category_granted_credits.length}
              name="productCategories"
              disabled
            />
          )}
          body={(
            <div className="flex flex-col gap-4">
              {branchData?.product_category_granted_credits?.map((category, index) => (
                <CreditProductCategoryConfig
                  key={index}
                  category={category?.product_category}
                  categoryIndex={index}
                  grantedCredit={category?.granted_credit}
                  grantedQuantity={category?.granted_quantity}
                  products={category?.product_granted_credits || []}
                  allowedCategoriesAndProductsSummary={allowedCategoriesAndProducts}
                  branchData={branchData}
                  companyKind={companyData?.kind}
                  controlledBy={controlledBy}
                />
              ))}
            </div>
          )}
        />
      ) : null}
    </div>
  );
};
